import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";

const Terms = () => {
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://namebiolab.lovable.app/terms#webpage",
          "url": "https://namebiolab.lovable.app/terms",
          "name": "Terms of Service - NameBioLab",
          "description": "Read the terms and conditions for using NameBioLab's AI-powered username and bio generator service.",
          "isPartOf": {
            "@id": "https://namebiolab.lovable.app/#webapp"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://namebiolab.lovable.app/terms#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://namebiolab.lovable.app/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Terms of Service",
              "item": "https://namebiolab.lovable.app/terms"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-6">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p>
              Welcome to NameBioLab. By accessing and using our AI-powered username and bio generator service, 
              you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, 
              please do not use our service. We reserve the right to modify these terms at any time, and your 
              continued use of the service constitutes acceptance of any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Service Description</h2>
            <p>
              NameBioLab is a free online tool that uses advanced artificial intelligence to generate 
              creative usernames and engaging bios for social media platforms including Instagram, TikTok, YouTube, 
              Twitter, and more. The service is provided "as is" without warranties of any kind. We strive to provide 
              high-quality, unique suggestions, but cannot guarantee availability or suitability for your specific needs.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. User Responsibilities</h2>
            <p>When using our service, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate information for generation purposes</li>
              <li>Not use generated content for illegal, harmful, or offensive purposes</li>
              <li>Not attempt to harm, disrupt, or interfere with our service</li>
              <li>Respect intellectual property rights</li>
              <li>Verify username availability on your target platform before use</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Intellectual Property</h2>
            <p>
              Generated usernames and bios are provided for your personal or commercial use. However, we cannot 
              guarantee their uniqueness or trademark availability. You are responsible for ensuring your chosen 
              username or bio does not infringe on existing rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Disclaimer of Warranties</h2>
            <p>
              Our service is provided on an "as is" and "as available" basis. We do not guarantee that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Generated content will be unique or available for use</li>
              <li>The service will be uninterrupted, secure, or error-free</li>
              <li>Results will meet your specific requirements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, NameBioLab shall not be liable for any 
              indirect, incidental, special, consequential, or punitive damages resulting from your use or 
              inability to use the service, including but not limited to loss of profits, data, or goodwill.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Advertising</h2>
            <p>
              Our website displays third-party advertisements. We are not responsible for the content of these 
              ads or the practices of advertisers. Clicking on ads is at your own risk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Changes to Terms</h2>
            <p>
              We reserve the right to modify these terms at any time. Continued use of the service after changes 
              constitutes acceptance of the modified terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Contact Information</h2>
            <p>
              For questions about these Terms of Use, please{" "}
              <Link to="/contact" className="text-primary hover:underline">
                contact us
              </Link>
              .
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default Terms;
